import { useRouter } from "vue-router";
import type { Module } from "vuex";
import { apiPaths } from "@/dictionaries/api/paths";
import http from "@/utils/axios";
import { notify } from "@/utils/notify";

export interface AppointmentHoursStore {
  meeting_interval: number;
  days: {
    enabled: boolean;
    day_of_week: number;
    hours: { from: string; to: string }[];
  }[];
}

type AppointmentHoursDay = AppointmentHoursStore["days"][number];

const defaultDays = Array.from({ length: 7 }, (_, idx) => ({
  enabled: idx < 5,
  day_of_week: idx + 1,
  hours: [{ from: "08:00", to: "16:00" }],
}));

const module: Module<AppointmentHoursStore, unknown> = {
  namespaced: true,
  state: {
    meeting_interval: 0,
    days: defaultDays,
  },
  getters: {
    getOpenHours: (state) => state.days,
    getMeetingInterval: (state) => state.meeting_interval,
  },
  mutations: {
    setMeetingInterval(state, value: number) {
      state.meeting_interval = value;
    },
    editDay(state, value: AppointmentHoursDay) {
      const editIdx = state.days.findIndex(
        (item) => item.day_of_week === value.day_of_week
      );
      if (editIdx === -1) return;

      state.days[editIdx] = value;
    },
    toggleEnabled(state, idx: number) {
      if (state.days[idx]) {
        state.days[idx].enabled = !state.days[idx].enabled;
      }
    },
    setDays(state, newState: AppointmentHoursStore["days"]) {
      state.days = newState;
    },
  },
  actions: {
    async saveOnServer(ctx) {
      const router = useRouter();
      const dataToSend = {
        meeting_interval: Number(ctx.state.meeting_interval) || 0,
        days: ctx.state.days.map((day) => ({
          day_of_week: day.day_of_week,
          hours: day.enabled
            ? day.hours.map((hours) => ({
                text: `${hours.from} - ${hours.to}`,
                ...hours,
              }))
            : [],
        })),
      };

      try {
        await http.post(apiPaths.calendar.schedule.open.exact, dataToSend);
        return true;
      } catch {
        notify("error.response_error", { type: "danger" });
        return false;
      }
    },
  },
};

export default module;
